import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../ui/dialog';
import { DollarSign, Plus, Edit, Trash2, Save } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL || "";

export const ServicePrices = () => {
  const [prices, setPrices] = useState([]);
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [editingType, setEditingType] = useState(null);
  const [form, setForm] = useState({ service_type: '', label: '', price: '' });

  const fetchPrices = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/prices`, { withCredentials: true });
      setPrices(data);
    } catch (error) { console.error(error); }
  }, []);

  useEffect(() => { fetchPrices(); }, [fetchPrices]);

  const handleSave = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API_URL}/api/prices`, {
        service_type: form.service_type.toLowerCase().replace(/\s+/g, '_'),
        label: form.label,
        price: parseFloat(form.price)
      }, { withCredentials: true });
      toast.success('Preço salvo!');
      setIsCreateOpen(false);
      setEditingType(null);
      setForm({ service_type: '', label: '', price: '' });
      fetchPrices();
    } catch (error) { toast.error('Erro ao salvar'); }
  };

  const handleDelete = async (serviceType) => {
    if (!window.confirm('Remover este preço?')) return;
    try {
      await axios.delete(`${API_URL}/api/prices/${serviceType}`, { withCredentials: true });
      toast.success('Preço removido');
      fetchPrices();
    } catch (error) { toast.error('Erro ao remover'); }
  };

  const startEdit = (p) => {
    setEditingType(p.service_type);
    setForm({ service_type: p.service_type, label: p.label, price: String(p.price) });
    setIsCreateOpen(true);
  };

  return (
    <div className="space-y-4" data-testid="service-prices">
      <div className="flex justify-between items-center">
        <h3 className="text-xl font-medium text-[#0F1B3D]" style={{ fontFamily: 'Outfit, sans-serif' }}>
          Tabela de Preços
        </h3>
        <Dialog open={isCreateOpen} onOpenChange={(open) => { setIsCreateOpen(open); if (!open) { setEditingType(null); setForm({ service_type: '', label: '', price: '' }); } }}>
          <DialogTrigger asChild>
            <Button variant="outline" size="sm" className="border-[#E5DCC8]" data-testid="add-price-button">
              <Plus className="h-4 w-4 mr-1" /> Novo Tipo
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-sm">
            <DialogHeader>
              <DialogTitle>{editingType ? 'Editar Preço' : 'Novo Tipo de Consulta'}</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSave} className="space-y-4">
              <div className="space-y-2">
                <Label>Nome do Serviço *</Label>
                <Input value={form.label} onChange={e => setForm({ ...form, label: e.target.value, service_type: editingType || e.target.value.toLowerCase().replace(/\s+/g, '_') })} required placeholder="Ex: Consulta Presencial" data-testid="price-label-input" />
              </div>
              <div className="space-y-2">
                <Label>Valor (R$) *</Label>
                <Input type="number" step="0.01" value={form.price} onChange={e => setForm({ ...form, price: e.target.value })} required data-testid="price-value-input" />
              </div>
              <Button type="submit" className="w-full bg-[#A8174E] hover:bg-[#8E1142] text-white" data-testid="price-submit-button">
                <Save className="h-4 w-4 mr-2" /> Salvar
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <p className="text-sm text-[#7A85A0]">
        Estes valores são usados automaticamente ao agendar consultas. O pagamento pendente é gerado com base no tipo selecionado.
      </p>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {prices.map((p) => (
          <Card key={p.service_type} className="border-[#E5DCC8]" data-testid={`price-card-${p.service_type}`}>
            <CardContent className="p-5">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-[#0F1B3D]/10 rounded-lg">
                    <DollarSign className="h-5 w-5 text-[#0F1B3D]" />
                  </div>
                  <div>
                    <p className="font-semibold text-[#0F1B3D]">{p.label}</p>
                    <p className="text-2xl font-bold text-[#0F1B3D]">R$ {p.price.toFixed(2)}</p>
                  </div>
                </div>
                <div className="flex gap-1">
                  <Button variant="ghost" size="sm" onClick={() => startEdit(p)} className="h-8 w-8 p-0" data-testid={`edit-price-${p.service_type}`}>
                    <Edit className="h-4 w-4 text-[#5F6B85]" />
                  </Button>
                  <Button variant="ghost" size="sm" onClick={() => handleDelete(p.service_type)} className="h-8 w-8 p-0" data-testid={`delete-price-${p.service_type}`}>
                    <Trash2 className="h-4 w-4 text-[#A8174E]" />
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
};
